import { observer } from 'mobx-react-lite';
import { FC, useEffect } from 'react';
import { Icon } from '@pypestream/design-system';

import { useBobcatFlag } from '../../hooks/use-bobcat-flag';
import { guidedSetupStore } from '../../stores/guided-setup-store';
import { tableStore } from '../../stores/table-store';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { coachState, stageIndex } from './guided-setup-model';

import './guided-setup-coach.scss';

/**
 * Project Bobcat (`bobcat-guided-setup`) — the guided setup's coach, as the
 * prototype draws it (`#setupCoach`): bottom left, the step, six progress
 * dots, the stage and one line on what to do there, with Exit and the one
 * next step. On Build's canvas it sits right of the agent list and above the
 * legend, so it covers neither.
 *
 * Whatever brings a stage up — the coach, or anything else that switches
 * stage, like opening an agent from Design — counts as reaching it, so a
 * stage you are on is never one the nav shows as locked.
 */
export const GuidedSetupCoach: FC<{ onCanvas: boolean }> = observer(
  ({ onCanvas }) => {
    const on = useBobcatFlag('guidedSetup') === 'allowed';
    const uniqueId = useProStudioCtxSelector(
      (ctx) => ctx.selectedBot?.uniqueId
    );
    const stage = tableStore.activeStage;
    const reached = on ? guidedSetupStore.reachedFor(uniqueId) : null;

    useEffect(() => {
      if (uniqueId && reached !== null) {
        guidedSetupStore.reach(uniqueId, stageIndex(stage));
      }
    }, [uniqueId, stage, reached]);

    if (!uniqueId || reached === null) return null;
    const coach = coachState(stage, reached);

    const goNext = () => {
      if (!coach.next) {
        guidedSetupStore.end(uniqueId);
        return;
      }
      guidedSetupStore.reach(uniqueId, stageIndex(coach.next));
      tableStore.setActiveStage(coach.next);
    };

    return (
      <aside
        className={`c-setup-coach${onCanvas ? ' c-setup-coach--canvas' : ''}`}
        aria-label="Guided setup"
        data-testid="bobcat-guided-setup"
      >
        <div className="c-setup-coach__top">
          <span className="c-setup-coach__step">{coach.step}</span>
          <span className="c-setup-coach__dots" aria-hidden>
            {coach.dots.map((done, i) => (
              <i key={i} className={done ? 'is-on' : undefined} />
            ))}
          </span>
        </div>
        <p className="c-setup-coach__title">{coach.title}</p>
        <p className="c-setup-coach__text">{coach.text}</p>
        <div className="c-setup-coach__foot">
          <button
            type="button"
            className="c-setup-coach__exit"
            data-testid="bobcat-guided-setup-exit"
            onClick={() => guidedSetupStore.end(uniqueId)}
          >
            Exit guided setup
          </button>
          <button
            type="button"
            className="c-setup-coach__next"
            data-testid="bobcat-guided-setup-next"
            onClick={goNext}
          >
            {coach.action}
            {coach.next && <Icon name="arrow-right" size="xsmall" />}
          </button>
        </div>
      </aside>
    );
  }
);
